import { FetchResult, ApolloCache, FieldMergeFunction, FieldFunctionOptions } from '@apollo/client';
import { CompanyIntegrationsQuery } from '@generated/CompanyIntegrationsQuery';
import { CreateBridgeIntegrationMutation } from '@generated/CreateBridgeIntegrationMutation';
import { PaginatedCallConversations } from '@generated/PaginatedCallConversations';
import { COMPANY_INTEGRATIONS_QUERY } from '@graphql/queries/CompanyIntegrationsQuery';
import uniqBy from 'lodash-es/uniqBy';

export interface SearchableCollection<T> {
  items?: T[];
  total: number;
}

/**
 * Add the newly created Bridge integration to the integrations list cache.
 * @param logo - logo of the integration
 * @returns a function that receives apollo's cache and the result from the fetch
 */
export function addBridgeIntegrationToListCache<T>(logo: string | null) {
  return (cache: ApolloCache<T>, result: FetchResult<CreateBridgeIntegrationMutation>): void => {
    const integrationsQueryCache = cache.readQuery<CompanyIntegrationsQuery>({
      query: COMPANY_INTEGRATIONS_QUERY,
    });
    if (integrationsQueryCache) {
      const { companyIntegrations } = integrationsQueryCache;
      const {
        id,
        active,
        customName,
        configurations,
        serviceName: name,
        serviceNameUnderscored: nameUnderscored,
      } = result.data!.createRestBridgeIntegration;
      cache.modify({
        fields: {
          companyIntegrations: () => ({
            ...companyIntegrations,
            data: [
              ...companyIntegrations.data,
              {
                ...result.data!.createRestBridgeIntegration,
                id: id.toString(),
                __typename: 'CompanyIntegration',
                attributes: {
                  __typename: 'RestIntegration',
                  id,
                  logo,
                  active,
                  customName,
                  name,
                  nameUnderscored,
                  numbersConnected: configurations!.numbers.length,
                  service: 'bridge',
                },
                type: 'integration',
              },
            ],
          }),
        },
      });
    }
  };
}

/**
 * Merge two arrays which share elements with given identifier.
 * @param identifier - key of the object
 * @returns the merged version of the two list if incoming is defined
 */
export function mergeListBy<T>(
  identifier: keyof T
): FieldMergeFunction<T[] | undefined, T[] | null> {
  // eslint-disable-next-line default-param-last
  return (existing = [], incoming) => {
    if (!incoming) {
      // Return existing if incoming value is null
      return existing;
    }

    const incomingIds = incoming.map((item) => item[identifier]);
    const baseData = existing.filter((item) => !incomingIds.includes(item[identifier]));

    return [...baseData, ...incoming];
  };
}

const defaultCheck = (args: Record<string, unknown>) => args.from === 0;
/**
 * Merge collection of search items together.
 * @param resetCacheValue - use to determine if the cache value for a query needs to start again
 * without the existing cache value based on the query argument
 *
 * Example: when we move to page with a query with 'cache-and-network' fetch policy then
 * if we move out / move in back to the page the query's cache should not merge again the
 * incoming result to the existing result
 *
 * @param existing - first collection
 * @param incoming - second collection
 * @param apolloFieldFunctionOptions - apollo's FieldFunctionOptions
 * @returns merged collection
 */
export const mergeSearchItemsCollection =
  (resetCacheValue = defaultCheck) =>
  <T>(
    existing: SearchableCollection<T>,
    incoming: SearchableCollection<T>,
    { args, field }: FieldFunctionOptions
  ): SearchableCollection<T> => {
    // unique behavior for field with alias
    if (field?.alias) {
      return incoming;
    }

    if (!incoming.items && existing?.items) {
      return { ...existing, ...incoming };
    }

    // if `incoming` doesn't have any items, skip merging
    if (!args || resetCacheValue(args) || !incoming.items) {
      return incoming;
    }

    const mergedItems = existing?.items ? existing.items.slice(0) : [];
    const start = args?.from || mergedItems.length;
    const end = start + incoming.items.length;

    // eslint-disable-next-line no-plusplus
    for (let i = start; i < end; i++) {
      mergedItems[i] = incoming.items[i - start];
    }

    return { ...incoming, items: mergedItems };
  };

/**
 * Merge list of call conversations together.
 * @param existing - current list
 * @param incoming - fetched list
 * @returns merged lists
 */
export function mergeCallConversations(
  existing: PaginatedCallConversations['getCallConversations'] | undefined,
  incoming: PaginatedCallConversations['getCallConversations']
): PaginatedCallConversations['getCallConversations'] {
  const shouldMerge = existing && existing.pageInfo?.nextToken === incoming!.pageInfo.currentToken;

  if (shouldMerge) {
    return {
      __typename: 'PaginatedCallConversations',
      pageInfo: incoming!.pageInfo,
      items: [...existing.items, ...incoming!.items],
    };
  }

  return incoming;
}

export interface UserAssociatedLinesCache {
  items: { __ref: string }[];
}

/**
 * Merge list of a user associated lines
 * @param existing - existing associated line items
 * @param incoming - incoming associated line items
 * @returns merge list
 */
export function mergeUserAssociatedLines(
  existing: UserAssociatedLinesCache | undefined,
  incoming: UserAssociatedLinesCache
): UserAssociatedLinesCache {
  const uniqueUserNumbers = uniqBy([...(existing?.items ?? []), ...incoming.items], '__ref');
  return { ...incoming, items: uniqueUserNumbers };
}
